import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Grid, Col, Row, Form, FormGroup, FormControl,
  ControlLabel, Button } from 'react-bootstrap'
import Radio from '../../components/CustomRadio/CustomRadio'
import Select from 'react-select'
import { useResponsive } from 'react-hooks-responsive'
import getLookups from '../Config/lookups.js'
import { constants as callTakersConstants } from '../../redux/callTakers/actions'
import { commonConstants } from '../../redux/common/actions'
import { getFormattedUser } from '../../services/auth'
import NotificationSystem from 'react-notification-system'
import { style as notificationStyling } from '../../variables/Variables.jsx'
import ClipLoader from 'react-spinners/ClipLoader'
import moment from 'moment-timezone'
import { coplineRoles } from '../Config/lookups.js'
import MandatoryLabel from 'views/Components/MandatoryLabel'

const states = require('../Config/usStates.json')
const provinces = require('../Config/canadaProvinces.json')
const australiaStates = require('../Config/australiaStates.json')
const Datetime = require('react-datetime')

const { countries } = getLookups()
const { REQUEST } = commonConstants
const { GET_CALL_TAKER, CREATE_CALL_TAKER, UPDATE_CALL_TAKER, UPDATE_USER_FORM_FIELD, RESET_USER_FORM_FIELDS } = callTakersConstants

const UserForm = ({ match }) => {
  const dispatch = useDispatch()
  const callTakersState = useSelector(state => state.callTakers)

  // Determine form source based on mode (editing vs creating)
  const isEditMode = Boolean(match.params.id && callTakersState.callTaker?.id)

  const form = callTakersState

  const loading = callTakersState.loading
  const error = callTakersState.error
  const success = callTakersState.success

  const notificationSystem = useRef()
  const [_notificationSystem, setNotificationSystem] = useState({})

  const searchParams = new URLSearchParams(window.location.search)
  const readOnlyMode = searchParams.has('readOnly') && searchParams.get('readOnly') === 'true'
  const submitRoute = match.params.id ? UPDATE_CALL_TAKER : CREATE_CALL_TAKER

  useEffect(() => {
    if (match.params.id) {
      dispatch({ type: GET_CALL_TAKER + REQUEST, payload: { id: match.params.id } })
    } else {
      dispatch({ type: RESET_USER_FORM_FIELDS, payload: { showMessage: false } })
    }
    setNotificationSystem(notificationSystem)
  }, [match.params.id])

  // Populate form with fetched call taker data in manageUsersState
  useEffect(() => {
    if (match.params.id && isEditMode && callTakersState.callTaker.id) {
      const transformedData = transformCallTakerToFormFields(callTakersState.callTaker)
      dispatch({
        type: RESET_USER_FORM_FIELDS,
        payload: {
          showMessage: false,
          fieldData: transformedData
        }
      })
    }
  }, [match.params.id, isEditMode, callTakersState.callTaker.id])

  const [, setHasAdmin] = useState(true)
  const [, setSelectedScheduleUser] = useState(null)
  useEffect(() => {
    const getAuth = async () => {
      const user = await getFormattedUser()
      const hasAdmin = user && user.roles && user.roles.includes('SuperAdmin')
      setHasAdmin(hasAdmin)
      setSelectedScheduleUser(user.id)
    }
    getAuth()
  }, [])

  useEffect(() => {
    const ns = _notificationSystem.current
    if (!ns) return

    if (success.message) {
      ns.addNotification({
        message: success.message,
        level: 'info',
        position: 'tc'
      })
    }

    if (error.message) {
      ns.addNotification({
        message: error.message,
        level: 'error',
        position: 'tc'
      })
    }
  }, [success.message, error])

  // Effect: Scroll to top when notifications appear
  // Note: Called twice to fix mobile scrolling bug
  // Mobile scrolls to top quickly then back to original position
  useEffect(() => {
    const element = document.getElementById('top-of-the-form-to-ya')
    if (element) {
      element.scrollIntoView(true)
      element.scrollIntoView(true)
    }
  }, [success.message, error])

  const {
    id, firstName, lastName, email, username, cellPhone, homePhone, birthDay, birthMonth,
    initialTrainingClassCity, initialTrainingClassDate, initialTrainingClassNumber,
    notes, availableForScheduling, roles, enabled, initialTrainingClassCountry, country,
    city, location, address, zip, initialTrainingClassLocation
  } = form

  const formattedPayload = {
    id, firstName, lastName, email, username, cellPhone, homePhone, birthDay, birthMonth,
    initialTrainingClassCity, initialTrainingClassDate, initialTrainingClassNumber,
    notes, availableForScheduling, roles, enabled, initialTrainingClassCountry, country,
    city, location, address, zip, initialTrainingClassLocation
  }

  const breakpoints = { xs: 0, sm: 425, md: 1024 }
  const { size } = useResponsive(breakpoints)
  const radioStyle = size === 'xs' ? { transform: 'scale(3)', margin: 16 } : {}

  const isCanada = country?.value === 'CANADA'
  const isAustralia = country?.value === 'AUSTRALIA'
  const provinceStateCollection = isCanada ? provinces : isAustralia ? australiaStates : states
  const provinceStateLabel = isCanada ? 'Province' : isAustralia ? 'State/Territory' : 'State'
  const zipPostalCodeLabel = isCanada ? 'Postal Code' : isAustralia ? 'Postcode' : 'Zip Code'
  const cellPhonePlaceholder = isAustralia ? 'e.g., 0412 345 678' : 'e.g., 555-123-4567'
  const homePhonePlaceholder = isAustralia ? 'e.g., (02) 1234 5678' : 'e.g., 555-987-6543'

  const updateLocationDefault = (event) => {
    const selectedCountry = event?.value
    let defaultRegion
    if (selectedCountry === 'CANADA') {
      defaultRegion = { label: 'Alberta', value: 'AB' }
    } else if (selectedCountry === 'AUSTRALIA') {
      defaultRegion = { label: 'Australian Capital Territory', value: 'ACT' }
    } else {
      defaultRegion = { label: 'Alabama', value: 'AL' }
    }
    handleChange(dispatch, defaultRegion, 'location')
  }

  if (loading.source === GET_CALL_TAKER) {
    return (
      <div className='main-content' style={{ textAlign: 'center' }}>
        <ClipLoader loading size={50} color="grey" />
      </div>
    )
  }

  return (
    <div className='main-content'>
      <NotificationSystem ref={notificationSystem} style={notificationStyling} />
      <Form>
        <Grid fluid>
          <Row>
            <Col xs={12} sm={6}>
              <FormGroup>
                <MandatoryLabel title="Username"/>
                <FormControl
                  type='text'
                  id='username'
                  name='username'
                  value={username}
                  placeholder='e.g., jsmith'
                  onChange={event => handleChange(dispatch, event)}
                  disabled={isEditMode}
                />
                {isEditMode ? (
                  <small style={{ color: '#999', fontStyle: 'italic', display: 'block', marginTop: '5px' }}>
                    Username cannot be modified
                  </small>
                ) : (
                  <small style={{ color: '#999', fontStyle: 'italic', display: 'block', marginTop: '5px' }}>
                    Username must be written in lowercase letters only
                  </small>
                )}
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={6}>
              <FormGroup>
                <MandatoryLabel title="Email"/>
                <FormControl
                  type='text'
                  id='email'
                  name='email'
                  value={email}
                  placeholder='e.g., johnsmith@example.com'
                  onChange={event => handleChange(dispatch, event)}
                />
              </FormGroup>
            </Col>
            <Col xs={12} sm={6}>
            <FormGroup>
                <MandatoryLabel title="Role Type"/>
                <Select
                  isMulti
                  options={coplineRoles}
                  value={roles}
                  name='roles'
                  onChange={event => {
                    // Allow clearing (event is null) or limiting to maximum 2 roles
                    if (!event || event.length <= 2) {
                      handleChange(dispatch, event, 'roles')
                    }
                  }}
                  isOptionDisabled={() => roles && roles.length >= 2}
                />
                <small style={{ color: '#999', fontStyle: 'italic', display: 'block', marginTop: '5px' }}>
                  You can select up to 2 roles
                </small>
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={6}>
              <FormGroup>
                <MandatoryLabel title="First Name"/>
                <FormControl
                  type='text'
                  id='firstName'
                  name='firstName'
                  value={firstName}
                  placeholder='e.g., John'
                  onChange={event => handleChange(dispatch, event)}
                />
              </FormGroup>
            </Col>
            <Col xs={12} sm={6}>
              <FormGroup>
                <MandatoryLabel title="Last Name"/>
                <FormControl
                  type='text'
                  id='lastName'
                  name='lastName'
                  value={lastName}
                  placeholder='e.g., Smith'
                  onChange={event => handleChange(dispatch, event)}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={4}>
              <FormGroup>
                <ControlLabel>City</ControlLabel>
                <FormControl
                  type='text'
                  id='city'
                  name='city'
                  value={city}
                  placeholder='e.g., Los Angeles'
                  onChange={event => handleChange(dispatch, event)}
                />
              </FormGroup>
            </Col>
            <Col xs={12} sm={4}>
              <FormGroup>
                <ControlLabel>Address</ControlLabel>
                <FormControl
                  type='text'
                  id='address'
                  name='address'
                  value={address}
                  placeholder='e.g., 123 Main Street'
                  onChange={event => handleChange(dispatch, event)}
                />
              </FormGroup>
            </Col>
            <Col xs={12} sm={4}>
              <FormGroup>
                <ControlLabel>{zipPostalCodeLabel}</ControlLabel>
                <FormControl
                  type='text'
                  id='zip'
                  name='zip'
                  value={zip}
                  placeholder={isCanada ? 'e.g., K1A 0B1' : isAustralia ? 'e.g., 2000' : 'e.g., 90001'}
                  onChange={event => handleChange(dispatch, event)}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
          <Col xs={12} sm={6}>
              <FormGroup>
                <MandatoryLabel title="Country"/>
                <Select
                  options={countries}
                  value={country}
                  name='country'
                  onChange={event => {
                    handleChange(dispatch, event, 'country')
                    updateLocationDefault(event)}
                  }
                />
              </FormGroup>
            </Col>
            <Col xs={12} sm={6}>
              <FormGroup>
                <ControlLabel>{provinceStateLabel}</ControlLabel>
                <Select
                  options={provinceStateCollection}
                  value={location}
                  name='location'
                  onChange={event => handleChange(dispatch, event, 'location')}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={6}>
              <FormGroup>
                <MandatoryLabel title="Cell Phone"/>
                <FormControl
                  type='text'
                  id='cellPhone'
                  name='cellPhone'
                  value={cellPhone}
                  placeholder={cellPhonePlaceholder}
                  onChange={event => handleChange(dispatch, event)}
                />
              </FormGroup>
            </Col>
            <Col xs={12} sm={6}>
              <FormGroup>
                <ControlLabel>Home Phone</ControlLabel>
                <FormControl
                  type='text'
                  id='homePhone'
                  name='homePhone'
                  value={homePhone}
                  placeholder={homePhonePlaceholder}
                  onChange={event => handleChange(dispatch, event)}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={6}>
              <FormGroup>
                <ControlLabel>Birth Day</ControlLabel>
                <FormControl
                  type='text'
                  id='birthDay'
                  name='birthDay'
                  value={birthDay}
                  placeholder='e.g., 15'
                  onChange={event => handleChange(dispatch, event)}
                />
              </FormGroup>
            </Col>
            <Col xs={12} sm={6}>
              <FormGroup>
                <ControlLabel>Birth Month</ControlLabel>
                <FormControl
                  type='text'
                  id='birthMonth'
                  name='birthMonth'
                  value={birthMonth}
                  placeholder='e.g., 7'
                  onChange={event => handleChange(dispatch, event)}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={6}>
              <FormGroup>
                <ControlLabel>Training City</ControlLabel>
                <FormControl
                  type='text'
                  id='initialTrainingClassCity'
                  name='initialTrainingClassCity'
                  value={initialTrainingClassCity}
                  placeholder='e.g., Los Angeles, CA'
                  onChange={event => handleChange(dispatch, event)}
                />
              </FormGroup>
            </Col>
            <Col xs={12} sm={6}>
              <FormGroup>
                <ControlLabel>Training Country</ControlLabel>
                <Select
                  options={countries}
                  value={initialTrainingClassCountry}
                  name='initialTrainingClassCountry'
                  onChange={event => handleChange(dispatch, event, 'initialTrainingClassCountry')}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={6}>
              <FormGroup>
                <ControlLabel>Training Class Number</ControlLabel>
                <FormControl
                  type='text'
                  id='initialTrainingClassNumber'
                  name='initialTrainingClassNumber'
                  value={initialTrainingClassNumber}
                  placeholder='e.g., 10'
                  onChange={event => handleChange(dispatch, event)}
                />
              </FormGroup>
            </Col>
            <Col xs={12} sm={6}>
              <FormGroup>
                <ControlLabel>Training Date</ControlLabel>
                <Datetime
                  id='initialTrainingClassDate'
                  name='initialTrainingClassDate'
                  value={initialTrainingClassDate ? moment.utc(initialTrainingClassDate).format() : moment.utc().format()}
                  timeFormat={false}
                  onChange={event => handleChange(dispatch, event, 'initialTrainingClassDate')}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={2}>
              <FormGroup>
              <MandatoryLabel title="Available For Scheduling"/>
              <Radio
                  number="availableForScheduling-true"
                  option={true}
                  name="availableForScheduling"
                  onChange={event => handleChange(dispatch, event)}
                  checked={availableForScheduling === true}
                  label="Yes"
                  style={radioStyle}
              />
              <Radio
                  number="availableForScheduling-false"
                  option={false}
                  name="availableForScheduling"
                  onChange={event => handleChange(dispatch, event)}
                  checked={availableForScheduling === false}
                  label="No"
                  style={radioStyle}
              />
              </FormGroup>
          </Col>
          <Col xs={12} sm={3}>
            <FormGroup>
            <MandatoryLabel title="Lock User Account"/>
            <Radio
                number="enabled-true"
                option={true}
                name="enabled"
                onChange={event => handleChange(dispatch, event)}
                checked={enabled === true}
                label="Enable User Account"
                style={radioStyle}
            />
            <Radio
                number="enabled-false"
                option={false}
                name="enabled"
                onChange={event => handleChange(dispatch, event)}
                checked={enabled === false}
                label="Disable User Account"
                style={radioStyle}
            />
            </FormGroup>
          </Col>
        </Row>
          <Row>
            <Col xs={12} sm={12}>
              <FormGroup>
                <ControlLabel>Notes</ControlLabel>
                <FormControl
                  componentClass='textarea'
                  id='notes'
                  name='notes'
                  value={notes}
                  placeholder='Additional notes about this user...'
                  style={{  height: '200px' }}
                  onChange={event => handleChange(dispatch, event)}
                />
              </FormGroup>
            </Col>
          </Row>
          {!readOnlyMode &&
            <Row style={{marginTop: 20}}>
              <Col xs={12} sm={12}>
                {!(loading.source === UPDATE_CALL_TAKER || loading.source === CREATE_CALL_TAKER ) &&
                  <Button
                    style={{ marginRight: '15px' }}
                    bsStyle='primary'
                    bsSize="lg"
                    onClick={() => dispatch({ type: submitRoute + REQUEST, payload: formattedPayload })}
                  >
                    {match.params.id ? 'Save' : 'Submit'}
                  </Button>
                }
                {(loading.source === UPDATE_CALL_TAKER || loading.source === CREATE_CALL_TAKER) &&
                <span style={{ padding: 40 }}>
                  <ClipLoader  loading size={50} color="grey" />
                </span>
                }
                {!match.params.id &&
                <Button
                  bsStyle='warning'
                  type='reset'
                  bsSize="lg"
                  onClick={() => dispatch({ type: RESET_USER_FORM_FIELDS, payload: {showMessage: true}})}
                >
                  Reset
                </Button>
                }
                {match.params.id &&
                  <Button
                    bsStyle='warning'
                    type='reset'
                    bsSize="lg"
                    onClick={() => {window.location.href = "/admin/users"}}
                  >
                    Cancel
                  </Button>
                }
              </Col>
            </Row>
          }
        </Grid>
      </Form>
    </div>
  )
}

function handleChange (dispatch, event, formField) {
  let field = null
  let value = null
  if (['initialTrainingClassDate'].includes(formField))  {
    field = formField
    value = event
  } else if (event && event.target) {
    field = event.target.name
    value = event.target.value
    value = value === "true" ? true : value
    value = value === "false" ? false : value
  } else { // select form items (event can be null when clearing)
    field = formField
    value = event
  }
  return dispatch({ type: UPDATE_USER_FORM_FIELD, field, value})
}

// Convert string value to dropdown option { label, value }
function transformToDropdown(value, options) {
  return value ? options.find(opt => opt.value === value) || null : null
}

// Convert array of string values to array of dropdown options
function transformToDropdownArray(values, options) {
  if (!values || !Array.isArray(values)) return []
  return values.map(value => options.find(opt => opt.value === value)).filter(Boolean)
}

// Get region options (states, provinces, or territories) based on country
function getRegionOptions(country) {
  if (country === 'CANADA') return provinces
  if (country === 'AUSTRALIA') return australiaStates
  return states
}

// Transform V2 API data to form format (flatten nested objects, convert to dropdown options)
function transformCallTakerToFormFields(callTaker) {
  return {
    ...callTaker,
    // V2 returns phone as {E164, national} objects — extract national for form fields
    cellPhone: callTaker.cellPhone?.national || callTaker.cellPhone || '',
    homePhone: callTaker.homePhone?.national || callTaker.homePhone || '',
    // V2 returns initialTrainingClass as nested object — flatten for form fields
    initialTrainingClassCity: callTaker.initialTrainingClass?.city || '',
    initialTrainingClassDate: callTaker.initialTrainingClass?.date || '',
    initialTrainingClassNumber: callTaker.initialTrainingClass?.number || '',
    initialTrainingClassCountry: transformToDropdown(
      callTaker.initialTrainingClass?.country,
      countries
    ),
    initialTrainingClassLocation: transformToDropdown(
      callTaker.initialTrainingClass?.location,
      getRegionOptions(callTaker.initialTrainingClass?.country)
    ),
    roles: transformToDropdownArray(callTaker.roles, coplineRoles),
    country: transformToDropdown(callTaker.country, countries),
    location: transformToDropdown(
      callTaker.location,
      getRegionOptions(callTaker.country)
    ),
  }
}

export default UserForm